/**
 * Matching react-query cache keys against an entity key.
 *
 * 🔴 The `typeof === "string"` check is the whole point of this file.
 *
 * `useEntity` names its queries with plain strings — `["container_table", filter]`,
 * `["container_detail", 7]` — and invalidates by substring. The generated TanStack helpers
 * in `src/api/queries` do not: hey-api's `createQueryKey()` puts an **object** at
 * `queryKey[0]`.
 *
 * Once any page rendered a component using one of those helpers, the old predicate
 *
 *     (query.queryKey[0] as string)?.indexOf(key) != -1
 *
 * threw `TypeError: q.queryKey[0].indexOf is not a function`. `invalidateQueries` runs the
 * predicate over the *whole* cache, so one object-keyed query killed **all** invalidation on
 * that page: edits saved but the UI never refreshed, and deletes never redirected because
 * the throw propagated out of the mutation's `onSettled`.
 *
 * The `as string` cast is what hid it — it silenced the only warning TypeScript could have
 * given.
 */
export function queryKeyMatchesEntity(queryKey: readonly unknown[], entityKey: string): boolean {
  const first = queryKey[0];
  return typeof first === "string" && first.indexOf(entityKey) !== -1;
}

/**
 * Does this query key belong to one of the given generated operations?
 *
 * The generated react-query helpers key on a single object whose `_id` is the SDK function
 * name — `[{ _id: "geozonesGetEntity", baseUrl, path, query }]`. Matching on it lets an
 * entity mutation invalidate the generated queries for that same entity, which the
 * string-based predicate above can never reach.
 */
export function queryKeyMatchesOperations(queryKey: readonly unknown[], operationIds: ReadonlySet<string>): boolean {
  const first = queryKey[0];
  if (typeof first !== "object" || first === null) return false;
  const id = (first as { _id?: unknown })._id;
  return typeof id === "string" && operationIds.has(id);
}
